import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Form,
  Nav,
  Modal,
} from "react-bootstrap";
import { Helmet } from "react-helmet";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import { saveAs } from "file-saver";
import jwt_decode from "jwt-decode";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { Editor, EditorProvider } from "react-simple-wysiwyg";
import schoolApi from "../../api/schoolApi";
import Confirm from "../Confirm";
import "react-toastify/dist/ReactToastify.css";

const AnnouncementDetail = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [announcement, setAnnouncement] = useState(null);
  const [files, setFiles] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  // fileId -> object URL (preview loaded through the files API, works on any host / port)
  const [blobUrls, setBlobUrls] = useState({});

  // Delete state
  const [deleteModalShow, setDeleteModalShow] = useState(false);

  // Edit state
  const [editModalShow, setEditModalShow] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [newUploadFiles, setNewUploadFiles] = useState([]);

  // Fetch tenant info
  const getTenantPrefix = () => {
    try {
      const token = sessionStorage.getItem("token");
      if (token) {
        const decoded = jwt_decode(token);
        return decoded?.tenantcode;
      }
    } catch (e) {
      console.error("Error decoding token:", e);
    }
    return null;
  };

  const tenantPrefix = getTenantPrefix();

  const buildFileUrl = (file) => {
    const blobKey = file?.file_id ?? file?.fileId ?? file?.id;
    if (blobKey && blobUrls[blobKey]) return blobUrls[blobKey];
    const timestamp = new Date().getTime();
    const fileParentId = file?.file_parent_id ?? file?.fileParentId ?? file?.parentid;
    const fileId = file?.file_id ?? file?.fileId ?? file?.id;
    const fileTypeExt = file?.file_type ?? file?.fileType ?? file?.filetype;

    if (fileParentId && fileId && fileTypeExt && tenantPrefix) {
      return `/uploads/${tenantPrefix}/${fileParentId}/${fileId}.${fileTypeExt}?v=${timestamp}`;
    }

    // files of this announcement are stored under the announcement id
    if (fileId && fileTypeExt && tenantPrefix) {
      return `/uploads/${tenantPrefix}/${id}/${fileId}.${fileTypeExt}?v=${timestamp}`;
    }

    return null;
  };

  const fetchAnnouncementData = async () => {
    try {
      setLoading(true);

      // 1. Fetch Announcement Basic Details
      const annRes = await schoolApi.fetchAnouncementById(id);
      // backend sends { result: { AllAnouncements: [ {...} ] } }
      const r = annRes?.result;
      const record = Array.isArray(r?.AllAnouncements)
        ? r.AllAnouncements.find((a) => String(a.id) === String(id)) || r.AllAnouncements[0]
        : Array.isArray(r) ? r[0] : r;
      if (annRes?.success && record) {
        setAnnouncement(record);
        setEditForm({
          title: record?.title || "",
          description: record?.description || "",
        });
      } else {
        toast.error(annRes?.message || "Announcement not found.");
      }

      // 2. Fetch Attached Files
      try {
        const fileRes = await schoolApi.fetchFiles(id);
        if (fileRes?.success && Array.isArray(fileRes?.result) && fileRes.result.length) {
          setFiles(fileRes.result);
        } else if (record?.file_id) {
          // fallback: the announcement record itself carries its (first) attachment
          const ext = String(record.file_title || "").split(".").pop().toLowerCase();
          setFiles([{ id: record.file_id, title: record.file_title, parentid: id, filetype: ext }]);
        } else {
          setFiles([]);
        }
      } catch (err) {
        console.error("Error fetching files:", err);
        setFiles([]);
      }

      // 3. Fetch Assigned Recipients
      try {
        const userRes = await schoolApi.getAnnouncementUsers(id);
        if (userRes?.success && userRes?.result) {
          setRecipients(userRes.result);
        } else {
          setRecipients([]);
        }
      } catch (err) {
        console.error("Error fetching announcement users:", err);
        setRecipients([]);
      }
    } catch (error) {
      console.error("Error loading announcement details:", error);
      toast.error(
        error?.response?.data?.message || "Failed to load announcement details."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAnnouncementData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // File Download Handler
  const handleDownloadFile = async (file) => {
    const fileId = file?.file_id || file?.id;
    const fileName = file?.title || file?.name || `attachment-${fileId}`;

    try {
      const response = await schoolApi.downloadFiles(fileId);
      const blob = new Blob([response], { type: response.type });
      saveAs(blob, fileName);
      toast.success("Download started...");
    } catch (error) {
      console.error("Error downloading file:", error);
      // Fallback: try opening direct URL
      const directUrl = buildFileUrl(file);
      if (directUrl) {
        window.open(directUrl, "_blank");
      } else {
        toast.error("Failed to download file.");
      }
    }
  };

  // Delete Announcement Handler
  const handleDeleteAnnouncement = async () => {
    try {
      const res = await schoolApi.deleteAnouncement(id);
      if (res?.success) {
        toast.success(res?.message || "Announcement deleted successfully.");
        setDeleteModalShow(false);
        navigate("/announcements");
      } else {
        toast.error(res?.message || "Failed to delete announcement.");
      }
    } catch (error) {
      console.error("Error deleting announcement:", error);
      toast.error(
        error?.response?.data?.message || "Failed to delete announcement."
      );
    }
  };

  // Quick Edit Submit Handler
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.title.trim()) {
      return toast.error("Title cannot be empty.");
    }

    try {
      setEditSubmitting(true);
      const updatePayload = {
        id: id,
        title: editForm.title.trim(),
        description: editForm.description || "",
      };

      const res = await schoolApi.updateAnouncement(updatePayload);
      if (res?.success) {
        // Upload additional files if selected
        if (newUploadFiles.length > 0) {
          for (let i = 0; i < newUploadFiles.length; i++) {
            let formData = new FormData();
            formData.append(`files${i}`, newUploadFiles[i]);
            formData.append("description", "");
            await schoolApi.createFile(id, formData);
          }
        }

        toast.success("Announcement updated successfully!");
        setEditModalShow(false);
        setNewUploadFiles([]);
        fetchAnnouncementData();
      } else {
        toast.error(res?.message || "Failed to update announcement.");
      }
    } catch (error) {
      console.error("Error updating announcement:", error);
      toast.error(
        error?.response?.data?.message || "Failed to update announcement."
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  // Helpers to categorize files
  const isImageFile = (file) => {
    const ext = (file?.filetype || file?.file_type || "").toLowerCase();
    return ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext);
  };

  const isVideoFile = (file) => {
    const ext = (file?.filetype || file?.file_type || "").toLowerCase();
    return ["mp4", "webm", "ogg", "mov", "avi"].includes(ext);
  };
  // load image / video previews through the files API
  useEffect(() => {
    let alive = true;
    const made = [];
    (async () => {
      const next = {};
      for (const f of files) {
        const fid = f?.file_id ?? f?.fileId ?? f?.id;
        if (!fid || !(isImageFile(f) || isVideoFile(f))) continue;
        try {
          const data = await schoolApi.downloadFiles(fid);
          if (data && data.size) {
            const url = URL.createObjectURL(data);
            made.push(url);
            next[fid] = url;
          }
        } catch (e) {
          console.error("Preview load failed:", e);
        }
      }
      if (alive) setBlobUrls(next);
    })();
    return () => {
      alive = false;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);



  const getFileIcon = (file) => {
    const ext = (file?.filetype || file?.file_type || "").toLowerCase();
    if (["pdf"].includes(ext)) {
      return <i className="fa-solid fa-file-pdf text-danger fs-3"></i>;
    }
    if (["doc", "docx"].includes(ext)) {
      return <i className="fa-solid fa-file-word text-primary fs-3"></i>;
    }
    if (["xls", "xlsx", "csv"].includes(ext)) {
      return <i className="fa-solid fa-file-excel text-success fs-3"></i>;
    }
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) {
      return <i className="fa-solid fa-file-image text-info fs-3"></i>;
    }
    if (["mp4", "webm", "ogg"].includes(ext)) {
      return <i className="fa-solid fa-file-video text-warning fs-3"></i>;
    }
    return <i className="fa-solid fa-file text-secondary fs-3"></i>;
  };

  // Recipient filtering
  const studentRecipients = recipients.filter(
    (r) => r.recordtype === "student"
  );
  const teacherRecipients = recipients.filter(
    (r) => r.recordtype === "teacher"
  );
  const otherRecipients = recipients.filter(
    (r) => r.recordtype !== "student" && r.recordtype !== "teacher"
  );

  const getFilteredRecipients = () => {
    let list = recipients;
    if (activeTab === "students") list = studentRecipients;
    if (activeTab === "teachers") list = teacherRecipients;
    if (activeTab === "others") list = otherRecipients;

    if (recipientSearch.trim()) {
      const q = recipientSearch.toLowerCase();
      list = list.filter((r) => {
        const name = (
          r.contactname ||
          `${r.firstname || ""} ${r.lastname || ""}`
        ).toLowerCase();
        const phone = (r.phone || r.whatsapp_number || "").toLowerCase();
        const cls = (r.classname || "").toLowerCase();
        const sec = (r.section_name || "").toLowerCase();
        return (
          name.includes(q) ||
          phone.includes(q) ||
          cls.includes(q) ||
          sec.includes(q)
        );
      });
    }

    return list;
  };

  const displayedRecipients = getFilteredRecipients();

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading announcement...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading announcement details...</p>
      </Container>
    );
  }

  if (!announcement) {
    return (
      <Container className="py-5 text-center">
        <div className="p-5 bg-white rounded-4 shadow-sm border">
          <i className="fa-solid fa-circle-exclamation text-danger fs-1 mb-3"></i>
          <h4>Announcement Not Found</h4>
          <p className="text-muted">
            The announcement you are looking for might have been deleted or is unavailable.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate("/announcements")}
            className="rounded-pill px-4 mt-2"
          >
            <i className="fa-solid fa-arrow-left me-2"></i>Back to Announcements
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{announcement?.title || "Announcement Details"}</title>
      </Helmet>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Delete Confirmation Modal */}
      {deleteModalShow && (
        <Confirm
          show={deleteModalShow}
          onHide={() => setDeleteModalShow(false)}
          handleDeleteButton={handleDeleteAnnouncement}
          title="Confirm Delete?"
          message="Are you sure you want to delete this announcement? This action cannot be undone."
          table="announcement"
        />
      )}

      {/* Edit Announcement Modal */}
      <Modal
        show={editModalShow}
        onHide={() => setEditModalShow(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 fw-bold text-primary mb-0">
            <i className="fa-regular fa-pen-to-square me-2"></i>Edit Announcement
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body className="p-4">
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">
                ANNOUNCEMENT TITLE *
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title"
                value={editForm.title}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">
                DESCRIPTION
              </Form.Label>
              <div
                className="border rounded-2 bg-white overflow-hidden"
                style={{ minHeight: "180px" }}
              >
                <EditorProvider>
                  <Editor
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                    style={{ minHeight: "180px", border: "none" }}
                  />
                </EditorProvider>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">
                ADD MORE ATTACHMENTS (OPTIONAL)
              </Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={(e) =>
                  setNewUploadFiles(Array.from(e.target.files))
                }
              />
              {newUploadFiles.length > 0 && (
                <div className="small text-muted mt-1">
                  {newUploadFiles.length} file(s) selected for upload.
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button
              variant="secondary"
              onClick={() => setEditModalShow(false)}
              disabled={editSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={editSubmitting}
              className="px-4"
            >
              {editSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Image Lightbox Modal */}
      {selectedImage && (
        <Modal
          show={!!selectedImage}
          onHide={() => setSelectedImage(null)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title className="h6 mb-0 text-truncate">
              {selectedImage.title || "Image Preview"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-2 bg-dark">
            <img
              src={selectedImage.url}
              alt={selectedImage.title || "Preview"}
              className="img-fluid rounded"
              style={{ maxHeight: "75vh", objectFit: "contain" }}
            />
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <span className="small text-muted">{selectedImage.title}</span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleDownloadFile(selectedImage.file)}
            >
              <i className="fa-solid fa-download me-1"></i>Download
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Container fluid className="px-lg-4 py-3">
        {/* Top Breadcrumbs & Action Bar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 pb-2 border-bottom">
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate("/announcements")}
              className="me-3 rounded-circle shadow-sm border bg-white"
              style={{ width: "36px", height: "36px" }}
              title="Back to Announcements"
            >
              <i className="fa-solid fa-arrow-left text-dark"></i>
            </Button>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0 align-items-center">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-muted text-decoration-none small">
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link
                    to="/announcements"
                    className="text-muted text-decoration-none small"
                  >
                    Announcements
                  </Link>
                </li>
                <li
                  className="breadcrumb-item active text-dark fw-bold small text-truncate"
                  style={{ maxWidth: "300px" }}
                >
                  {announcement.title}
                </li>
              </ol>
            </nav>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate("/announcements")}
              className="rounded-pill px-3"
            >
              <i className="fa-solid fa-list me-1"></i>All Announcements
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setEditModalShow(true)}
              className="rounded-pill px-3"
            >
              <i className="fa-regular fa-pen-to-square me-1"></i>Edit
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => setDeleteModalShow(true)}
              className="rounded-pill px-3"
            >
              <i className="fa-regular fa-trash-can me-1"></i>Delete
            </Button>
          </div>
        </div>

        {/* Hero Banner / Overview Card */}
        <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden bg-white">
          <Card.Body className="p-4">
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
              <div className="d-flex align-items-start gap-3">
                <div
                  className="rounded-4 p-3 d-flex align-items-center justify-content-center shadow-sm"
                  style={{
                    backgroundColor: "#e8f0fe",
                    color: "#1a73e8",
                    width: "56px",
                    height: "56px",
                  }}
                >
                  <i className="fa-solid fa-bullhorn fs-4"></i>
                </div>
                <div>
                  <h3 className="fw-bold text-dark mb-1">
                    {announcement.title}
                  </h3>
                  <div className="d-flex flex-wrap align-items-center gap-3 text-muted small mt-2">
                    {announcement.createddate && (
                      <span className="d-flex align-items-center">
                        <i className="fa-regular fa-calendar-days text-primary me-1"></i>
                        {moment(announcement.createddate).format(
                          "DD MMMM, YYYY · hh:mm A"
                        )}
                      </span>
                    )}
                    <span className="d-flex align-items-center">
                      <i className="fa-solid fa-users text-success me-1"></i>
                      {recipients.length > 0
                        ? `${recipients.length} Target Recipients`
                        : "Broadcast (All Members)"}
                    </span>
                    <span className="d-flex align-items-center">
                      <i className="fa-solid fa-paperclip text-info me-1"></i>
                      {files.length} Attachment(s)
                    </span>
                  </div>
                </div>
              </div>

              <div>
                {recipients.length === 0 ? (
                  <Badge bg="success" className="px-3 py-2 fs-6 rounded-pill">
                    <i className="fa-solid fa-earth-americas me-1"></i> Global Broadcast
                  </Badge>
                ) : (
                  <Badge bg="primary" className="px-3 py-2 fs-6 rounded-pill">
                    <i className="fa-solid fa-user-group me-1"></i> Targeted Audience
                  </Badge>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Main Content Layout */}
        <Row className="g-4">
          {/* Left Column: Announcement Body & Media */}
          <Col lg={7} xl={8}>
            {/* Announcement Description Card */}
            <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden bg-white">
              <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center">
                <i className="fa-solid fa-align-left text-primary me-2 fs-5"></i>
                <h5 className="mb-0 fw-bold text-dark">Announcement Content</h5>
              </Card.Header>
              <Card.Body className="p-4">
                {announcement.description ? (
                  <div
                    className="announcement-html-body p-4 rounded-3"
                    style={{
                      backgroundColor: "#fcfcfd",
                      border: "1px solid #edf2f7",
                      minHeight: "180px",
                      lineHeight: "1.7",
                      fontSize: "1rem",
                      color: "#2d3748",
                      wordBreak: "break-word",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: announcement.description,
                    }}
                  />
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fa-regular fa-comment-dots fs-1 mb-2 text-secondary opacity-50"></i>
                    <p className="mb-0">No description provided for this announcement.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Attachments & Media Card */}
            <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden bg-white">
              <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="fa-solid fa-paperclip text-primary me-2 fs-5"></i>
                  <h5 className="mb-0 fw-bold text-dark">
                    Attachments & Media ({files.length})
                  </h5>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {files.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fa-regular fa-folder-open fs-2 mb-2 text-secondary opacity-50"></i>
                    <p className="mb-0 small">No media files or documents attached.</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {files.map((file, idx) => {
                      const fileUrl = buildFileUrl(file);
                      const isImg = isImageFile(file);
                      const isVid = isVideoFile(file);

                      return (
                        <Col key={file.id || idx} xs={12} sm={6} md={isImg || isVid ? 6 : 12}>
                          <div
                            className="p-3 rounded-3 border bg-light h-100 d-flex flex-column justify-content-between shadow-sm transition-all hover-shadow"
                            style={{ border: "1px solid #e2e8f0" }}
                          >
                            {/* Image Preview */}
                            {isImg && fileUrl && (
                              <div
                                className="mb-3 rounded overflow-hidden text-center bg-white border position-relative"
                                style={{ height: "180px", cursor: "pointer" }}
                                onClick={() =>
                                  setSelectedImage({
                                    url: fileUrl,
                                    title: file.title || "Image Preview",
                                    file: file,
                                  })
                                }
                                title="Click to enlarge image"
                              >
                                <img
                                  src={fileUrl}
                                  alt={file.title || "attachment"}
                                  className="w-100 h-100"
                                  style={{ objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/user_images/profile.png";
                                  }}
                                />
                                <div
                                  className="position-absolute bottom-0 start-0 end-0 p-1 bg-dark bg-opacity-50 text-white small d-flex align-items-center justify-content-center gap-1"
                                >
                                  <i className="fa-solid fa-magnifying-glass-plus"></i>
                                  <span>View Image</span>
                                </div>
                              </div>
                            )}

                            {/* Video Preview */}
                            {isVid && fileUrl && (
                              <div className="mb-3 rounded overflow-hidden bg-black text-center">
                                <video
                                  src={fileUrl}
                                  controls
                                  className="w-100"
                                  style={{ maxHeight: "200px" }}
                                />
                              </div>
                            )}

                            {/* File Info & Action */}
                            <div className="d-flex align-items-center justify-content-between gap-2 mt-auto">
                              <div className="d-flex align-items-center gap-2 text-truncate">
                                {!isImg && !isVid && (
                                  <div className="me-1">{getFileIcon(file)}</div>
                                )}
                                <div className="text-truncate">
                                  <div
                                    className="fw-bold small text-dark text-truncate"
                                    title={file.title}
                                  >
                                    {file.title || `Attachment ${idx + 1}`}
                                  </div>
                                  <span className="badge bg-secondary text-uppercase" style={{ fontSize: "10px" }}>
                                    {file.filetype || file.file_type || "FILE"}
                                  </span>
                                </div>
                              </div>

                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="rounded-pill px-3 text-nowrap"
                                onClick={() => handleDownloadFile(file)}
                                title="Download File"
                              >
                                <i className="fa-solid fa-download me-1"></i>
                                Download
                              </Button>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column: Audience / Assigned Recipients */}
          <Col lg={5} xl={4}>
            <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden bg-white">
              <Card.Header className="bg-white border-bottom py-3 px-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <i className="fa-solid fa-users text-primary me-2 fs-5"></i>
                    <h5 className="mb-0 fw-bold text-dark">Recipients</h5>
                  </div>
                  <Badge bg="primary" className="rounded-pill px-3 py-1">
                    {recipients.length}
                  </Badge>
                </div>

                {/* Filter Tabs */}
                {recipients.length > 0 && (
                  <Nav
                    variant="pills"
                    className="nav-fill small gap-1 bg-light p-1 rounded-3 mt-2"
                  >
                    <Nav.Item>
                      <Nav.Link
                        active={activeTab === "all"}
                        onClick={() => setActiveTab("all")}
                        className="py-1 px-2 rounded-2"
                        style={{ cursor: "pointer", fontSize: "12px" }}
                      >
                        All ({recipients.length})
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        active={activeTab === "students"}
                        onClick={() => setActiveTab("students")}
                        className="py-1 px-2 rounded-2"
                        style={{ cursor: "pointer", fontSize: "12px" }}
                      >
                        Students ({studentRecipients.length})
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        active={activeTab === "teachers"}
                        onClick={() => setActiveTab("teachers")}
                        className="py-1 px-2 rounded-2"
                        style={{ cursor: "pointer", fontSize: "12px" }}
                      >
                        Teachers ({teacherRecipients.length})
                      </Nav.Link>
                    </Nav.Item>
                    {otherRecipients.length > 0 && (
                      <Nav.Item>
                        <Nav.Link
                          active={activeTab === "others"}
                          onClick={() => setActiveTab("others")}
                          className="py-1 px-2 rounded-2"
                          style={{ cursor: "pointer", fontSize: "12px" }}
                        >
                          Others ({otherRecipients.length})
                        </Nav.Link>
                      </Nav.Item>
                    )}
                  </Nav>
                )}
              </Card.Header>

              <Card.Body className="p-3">
                {recipients.length === 0 ? (
                  <div className="text-center py-5">
                    <div
                      className="rounded-circle p-3 d-inline-flex align-items-center justify-content-center bg-light text-success mb-3"
                      style={{ width: "64px", height: "64px" }}
                    >
                      <i className="fa-solid fa-bullhorn fs-3"></i>
                    </div>
                    <h6 className="fw-bold text-dark">Broadcast Announcement</h6>
                    <p className="text-muted small px-3">
                      This announcement was broadcast to all students, teachers, and school members.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Search box */}
                    <div className="mb-3">
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Search recipient by name, phone, class..."
                        value={recipientSearch}
                        onChange={(e) => setRecipientSearch(e.target.value)}
                        className="rounded-pill px-3 shadow-none border"
                      />
                    </div>

                    {displayedRecipients.length === 0 ? (
                      <div className="text-center py-4 text-muted small">
                        No recipients match your search.
                      </div>
                    ) : (
                      <div
                        className="recipient-list overflow-auto"
                        style={{ maxHeight: "550px" }}
                      >
                        {displayedRecipients.map((user, idx) => {
                          const fullName =
                            user.contactname ||
                            `${user.firstname || ""} ${user.lastname || ""}`.trim() ||
                            "Unnamed User";

                          const isStudent = user.recordtype === "student";
                          const isTeacher = user.recordtype === "teacher";

                          return (
                            <div
                              key={user.id || user.user_id || idx}
                              className="d-flex align-items-center justify-content-between p-2 mb-2 rounded-3 border bg-light"
                            >
                              <div className="d-flex align-items-center gap-2 overflow-hidden">
                                <div style={{ flexShrink: 0 }}>
                                  <NameInitialsAvatar
                                    name={fullName}
                                    size="36px"
                                    textSize="13px"
                                  />
                                </div>
                                <div className="text-truncate">
                                  <div className="fw-bold small text-dark text-truncate">
                                    {fullName}
                                  </div>
                                  <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "11px" }}>
                                    {isStudent && user.classname && (
                                      <span>
                                        Class {user.classname}
                                        {user.section_name ? ` - ${user.section_name}` : ""}
                                      </span>
                                    )}
                                    {user.phone && (
                                      <span>
                                        <i className="fa-solid fa-phone ms-1 me-1" style={{ fontSize: "10px" }}></i>
                                        {user.phone}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <Badge
                                bg={
                                  isStudent
                                    ? "info"
                                    : isTeacher
                                    ? "primary"
                                    : "secondary"
                                }
                                className="text-capitalize ms-2"
                                style={{ fontSize: "10px" }}
                              >
                                {user.recordtype || "User"}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AnnouncementDetail;
